import { X, Receipt, CheckCircle2, Calendar, ExternalLink, RefreshCcw, AlertCircle, Car, FileCheck } from 'lucide-react'
import { formatCurrency, formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import { FaturaExpanded } from '@/services/fatura.service'

interface Props {
    fatura: FaturaExpanded | null
    onClose: () => void
    onMarcarPaga?: (id: string) => void
    onGerarProRata?: (id: string) => void
}

export function FaturaDetailDrawer({ fatura, onClose, onMarcarPaga, onGerarProRata }: Props) {
    if (!fatura) return null
    const f = fatura
    const meta = (f as any).metadata || {}
    const hoje = new Date().toISOString().split('T')[0] || ''
    const isVencida = f.status === 'pendente' && f.data_vencimento < hoje
    const isAtrasada = f.status === 'atrasada' || isVencida
    const isPendente = f.status === 'pendente' && !isVencida

    // URLs — usa coluna dedicada ou fallback p/ metadata RAW do Asaas
    const urlFatura = f.url_pix || meta.invoiceUrl || null
    const urlBoleto = f.url_boleto || meta.bankSlipUrl || null
    const urlReceipt = meta.transactionReceiptUrl || null

    // Extrai placa em tempo real: prioriza "Placa: XXX" na descrição, depois regex
    function extrairPlacaFrontend(desc?: string, extRef?: string): string | null {
        const PLACA = /\b([A-Z]{3}[0-9][A-Z0-9][0-9]{2})\b/i
        if (desc) {
            const m = desc.match(/[Pp]laca[:\s]+([A-Z]{3}[0-9][A-Z0-9][0-9]{2})/i)
            if (m) return m[1]!.toUpperCase()
            const m2 = desc.match(PLACA)
            if (m2) return m2[1]!.toUpperCase()
        }
        if (extRef) {
            const m = extRef.match(PLACA)
            if (m) return m[1]!.toUpperCase()
        }
        return meta._placa_extraida || null
    }
    const placaExtraida = extrairPlacaFrontend(meta.description, meta.externalReference)

    // Descrição do boleto
    const descricao = meta.description || meta.externalReference || null

    return (
        <div className="fixed inset-0 z-50 flex justify-end animate-fade-in">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto animate-slide-in-right flex flex-col">
                <div className="sticky top-0 bg-white border-b border-surface-100 p-4 flex items-center justify-between z-10">
                    <h3 className="text-lg font-bold text-surface-900">Detalhes da Fatura</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-700 transition-colors">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-6 space-y-8 flex-1 text-center">
                    {/* Status e Montante */}
                    <div className="space-y-3">
                        <span className={`badge ${getStatusBadgeClass(f.status)}`}>{translateStatus(f.status)}</span>
                        <p className="text-4xl font-bold text-surface-900">{formatCurrency(f.valor)}</p>
                        <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">
                            {f.tipo === 'mensalidade' ? 'Mensalidade' : f.tipo === 'adesao' ? 'Taxa de Adesão' : f.tipo?.replace('_', ' ')}
                        </p>
                    </div>

                    {/* Alerta de Atraso */}
                    {isAtrasada && (
                        <div className="p-4 rounded-3xl bg-danger-50 border border-danger-100 flex items-center gap-4 text-left">
                            <AlertCircle size={20} className="text-danger-600 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-bold text-danger-700">Fatura Atrasada</p>
                                <p className="text-xs text-danger-600/70">Deseja gerar um valor pro-rata (proporcional) para hoje?</p>
                            </div>
                        </div>
                    )}

                    {/* Grid de Informações */}
                    <div className="grid grid-cols-1 gap-4 text-left">
                        <InfoRow icon={Calendar} label="Vencimento Original" value={formatDate(f.data_vencimento)} />
                        {f.data_pagamento && (
                            <InfoRow icon={CheckCircle2} label="Data do Pagamento" value={formatDate(f.data_pagamento)} color="text-green-600" />
                        )}
                        <InfoRow icon={Receipt} label="Vínculo / Contrato" value={f.contrato_ref || 'Serviços Diversos'} />
                        {placaExtraida && (
                            <InfoRow icon={Car} label="Placa do Veículo" value={placaExtraida} />
                        )}
                        {descricao && (
                            <InfoRow icon={Receipt} label="Descrição" value={descricao} />
                        )}
                        {urlFatura && (
                            <InfoRowLink icon={ExternalLink} label="Ver no Asaas" href={urlFatura} />
                        )}
                        {urlBoleto && urlBoleto !== urlFatura && (
                            <InfoRowLink icon={ExternalLink} label="Boleto PDF" href={urlBoleto} />
                        )}
                        {urlReceipt && (
                            <InfoRowLink icon={FileCheck} label="Comprovante de Pagamento" href={urlReceipt} />
                        )}
                    </div>

                    {/* Associado */}
                    <div className="pt-6 border-t border-surface-100 text-left">
                        <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-widest mb-4">Pagador</p>
                        <div className="p-4 rounded-2xl bg-surface-50">
                            <p className="text-sm font-bold text-surface-900">{f.associado_nome || 'Associado'}</p>
                            <p className="text-xs text-surface-700/60 mt-0.5">ID: {f.associado_id?.slice(0, 8)}</p>
                        </div>
                    </div>
                </div>

                {/* Ações Dinâmicas */}
                <div className="sticky bottom-0 bg-white p-4 border-t border-surface-100 flex flex-col gap-3">
                    {(isPendente || isAtrasada) && onMarcarPaga && (
                        <button
                            onClick={() => onMarcarPaga(f.id)}
                            className="btn btn-primary btn-lg w-full gap-2 shadow-lg shadow-primary-200"
                        >
                            <CheckCircle2 size={18} /> Confirmar Pagamento
                        </button>
                    )}

                    {isAtrasada && onGerarProRata && (
                        <button
                            onClick={() => onGerarProRata(f.id)}
                            className="btn btn-secondary btn-lg w-full gap-2 border-danger-200 text-danger-600 hover:bg-danger-50"
                        >
                            <RefreshCcw size={18} /> Gerar Pro-rata (Recalcular)
                        </button>
                    )}

                    <button onClick={onClose} className="btn btn-md text-surface-700 hover:bg-surface-50">Fechar</button>
                </div>
            </div>
        </div>
    )
}

function InfoRow({ icon: Icon, label, value, color }: { icon: any; label: string; value: string; color?: string }) {
    return (
        <div className="flex items-start gap-4 p-4 rounded-2xl border border-surface-100 bg-white shadow-sm">
            <div className={`p-2 rounded-xl bg-surface-50 flex-shrink-0 ${color || 'text-surface-700/40'}`}>
                <Icon size={18} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">{label}</p>
                <p className={`text-sm font-bold break-words whitespace-pre-wrap ${color || 'text-surface-900'}`}>{value}</p>
            </div>
        </div>
    )
}

function InfoRowLink({ icon: Icon, label, href }: { icon: any; label: string; href: string }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 rounded-2xl border border-primary-100 bg-white shadow-sm hover:bg-primary-50 transition-colors group"
            onClick={e => e.stopPropagation()}
        >
            <div className="p-2 rounded-xl bg-primary-50 text-primary-600">
                <Icon size={18} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">{label}</p>
                <p className="text-sm font-bold text-primary-600 underline truncate group-hover:text-primary-700">
                    Abrir link ↗
                </p>
            </div>
        </a>
    )
}
